import { Tooltip } from "@xola/ui-kit";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { DateFormat, formatDate } from "common";
import React, { useMemo, useRef } from "react";
import { Purchase, PurchaseItemObject, PurchaseStatus } from "types";
import shallow from "zustand/shallow";
import { AmountDueCellRenderer } from "../Roster/RosterGuestList/AmountDueCell";
import { RowData } from "../Roster/RosterGuestList/types";
import { FulfillmentCellRenderer } from "./FulfillmentCell";
import { MerchandiseActionCellRenderer } from "./MerchandiseActionCell";
import { SourceIcon } from "@/components/Common/SourceIcon";
import { useInfiniteCollection } from "@/hooks/useInfiniteCollection";
import {
    getMerchandiseFulfillmentCounts,
    getMerchandisePurchaseItems,
    getNonCanceledMerchandisePurchaseItems,
} from "@/models/MerchandisePurchaseItem";
import { useDashboardFilterStore, useModalStore } from "@/stores/dashboardFilterStore";
import { canViewPurchase } from "@/stores/globalStore";
import {
    MerchandiseDashboardFulfillmentFilter,
    useMerchandiseDashboardStore,
} from "@/stores/merchandiseDashboardStore";
import { usePurchaseDrawerStore } from "@/stores/purchaseDrawerStore";
import { getSeller } from "@/utils/getSeller";

export interface MerchandiseOrdersRowData {
    id: string;
    orderId: string;
    orderDate: string;
    itemShortCodes: string[];
    customerName: string;
    balance: number;
    itemsFulfilled: string;
    channel: string;
    purchase: Purchase;
}

const handlePurchaseClick = (purchase?: Purchase) => {
    if (!canViewPurchase() || !purchase) return;

    const merchandiseItems = getMerchandisePurchaseItems(purchase);
    const firstItemId = merchandiseItems[0]?.id ?? purchase.items?.[0]?.id;
    const contactId = purchase.organizer?.id;

    if (purchase.id && firstItemId) {
        const actions = usePurchaseDrawerStore.getState().actions;
        actions.setPurchaseId(purchase.id);
        actions.setPurchaseItemId(firstItemId);
        actions.setContactId(contactId ?? null);
    }
};

interface PurchaseIdCellRendererParams extends ICellRendererParams<MerchandiseOrdersRowData> {
    onClick?: (purchase?: Purchase) => void;
    className?: string;
}

const PurchaseIdCellRenderer = React.memo(({ data, onClick, className }: PurchaseIdCellRendererParams) => {
    if (!data?.purchase) return null;

    const visible = data.itemShortCodes.slice(0, 2).map((code) => code.toUpperCase());
    const overflow = data.itemShortCodes.slice(2).map((code) => code.toUpperCase());

    if (visible.length === 0) return null;

    return (
        <div className="flex items-center space-x-1">
            <span className={className} onClick={onClick ? () => onClick(data.purchase) : undefined}>
                {visible.join(", ")}
            </span>
            {overflow.length > 0 && (
                <Tooltip content={overflow.join(", ")}>
                    <span className="cursor-default text-sm text-gray-dark">+{overflow.length}</span>
                </Tooltip>
            )}
        </div>
    );
});

export const useMerchandiseOrdersData = () => {
    const seller = getSeller();
    const isModalOpen = useModalStore((state) => state.isOpen);
    const date = useDashboardFilterStore((state) => state.date);
    const filteredPurchasesRef = useRef<Purchase[]>();

    const [selectedProducts, fulfillmentStatus] = useMerchandiseDashboardStore(
        (state) => [state.filters.merchandise.selected, state.filters.fulfillmentStatus],
        shallow,
    );

    const queryParams = useMemo(() => {
        const params: Record<string, string> = {
            seller: seller.id,
            limit: "100",
            "createdAt[range]": `${date.startOf("day").toISOString()},${date.endOf("day").toISOString()}`,
            "items.object[in]": PurchaseItemObject.WARE,
            expand: "items,customer",
            status: PurchaseStatus.COMMITTED,
        };

        if (selectedProducts.length > 0) {
            params["items.product[in]"] = selectedProducts.join(",");
        }

        return params;
    }, [seller.id, date, selectedProducts]);

    const { data: purchases, isLoading } = useInfiniteCollection<Purchase>("purchases", queryParams, {
        enabled: !!seller.id,
        refetchOnMount: "always",
    });

    const filteredPurchases = useMemo(() => {
        let activePurchases = purchases.filter(
            (purchase) => getNonCanceledMerchandisePurchaseItems(purchase).length > 0,
        );

        if (!fulfillmentStatus.all && fulfillmentStatus.selected.length > 0) {
            activePurchases = activePurchases.filter((purchase) => {
                const { fulfilled: fulfilledUnits, total: totalUnits } = getMerchandiseFulfillmentCounts(purchase);

                const overallStatus =
                    totalUnits > 0 && fulfilledUnits >= totalUnits
                        ? MerchandiseDashboardFulfillmentFilter.FULFILLED
                        : fulfilledUnits === 0
                        ? MerchandiseDashboardFulfillmentFilter.UNFULFILLED
                        : MerchandiseDashboardFulfillmentFilter.PARTIALLY_FULFILLED;

                return fulfillmentStatus.selected.includes(overallStatus);
            });
        }

        if (isModalOpen) {
            return filteredPurchasesRef.current ?? activePurchases;
        }

        filteredPurchasesRef.current = activePurchases;
        return activePurchases;
    }, [purchases, isModalOpen, fulfillmentStatus]);

    const rowData = useMemo((): MerchandiseOrdersRowData[] => {
        return filteredPurchases.map((purchase) => {
            const { fulfilled: fulfilledItems, total: totalItems } = getMerchandiseFulfillmentCounts(purchase);
            const merchandiseItems = getMerchandisePurchaseItems(purchase);

            return {
                id: purchase.id,
                orderId: purchase.id,
                orderDate: formatDate(purchase.createdAt, DateFormat.DATE_TIME_SHORT),
                itemShortCodes: merchandiseItems.map((item) => item.shortCode),
                customerName: purchase.customerName ?? "",
                balance: purchase.balance ?? 0,
                itemsFulfilled: `${fulfilledItems}/${totalItems}`,
                channel: String(purchase.source ?? "Office"),
                purchase,
            };
        });
    }, [filteredPurchases]);

    const isPurchaseClickable = canViewPurchase();
    const clickableClassName = isPurchaseClickable ? "cursor-pointer text-primary underline hover:no-underline" : "";

    const columnDefs = useMemo((): ColDef<MerchandiseOrdersRowData>[] => {
        return [
            {
                headerName: "Actions",
                colId: "actions",
                minWidth: 50,
                maxWidth: 80,
                sortable: false,
                lockPosition: true,
                pinned: "left",
                cellClass: "text-center",
                cellRenderer: MerchandiseActionCellRenderer,
            },
            {
                headerName: "Confirmation Code",
                valueGetter: (params) => params.data?.itemShortCodes.slice(0, 2).join(", ").toUpperCase(),
                width: 150,
                cellRenderer: PurchaseIdCellRenderer,
                cellRendererParams: {
                    onClick: isPurchaseClickable ? handlePurchaseClick : undefined,
                    className: clickableClassName,
                },
            },
            {
                headerName: "Purchase Date",
                field: "orderDate",
                colId: "orderDate",
                width: 180,
                sortable: true,
            },
            {
                headerName: "Customer Name",
                field: "customerName",
                flex: 1,
                minWidth: 150,
                sortable: true,
                valueFormatter: (params) => params.value?.trim() || "N/A",
            },
            {
                headerName: "Balance Due",
                field: "balance",
                width: 100,
                minWidth: 60,
                cellRenderer: (params: ICellRendererParams<MerchandiseOrdersRowData>) => {
                    const data = {
                        path: [],
                        isOrganizer: true,
                        item: {
                            purchase: params.data?.purchase,
                        },
                    } as unknown as RowData;
                    return <AmountDueCellRenderer data={data} />;
                },
            },
            {
                headerName: "Items Fulfilled",
                field: "itemsFulfilled",
                flex: 2,
                minWidth: 150,
                cellRenderer: FulfillmentCellRenderer,
            },
            {
                headerName: "Channel",
                field: "channel",
                flex: 1,
                minWidth: 120,
                cellRenderer: (params: ICellRendererParams<MerchandiseOrdersRowData>) => {
                    const source = params.data?.purchase?.source ?? "office";
                    return (
                        <div className="flex items-center space-x-2">
                            <SourceIcon source={source} size="small" />
                            <span className="capitalize">{params.value}</span>
                        </div>
                    );
                },
            },
        ];
    }, [clickableClassName, isPurchaseClickable]);

    const hasNoData = rowData.length === 0 && !isLoading;

    return {
        rowData,
        columnDefs,
        isLoading,
        hasNoData,
        purchases: filteredPurchases,
    };
};
